import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import {
  X,
  Heart,
  MessageCircle,
  UserPlus,
  Users,
  Bell,
  Share2,
  Star,
  Check,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import {
  useNotifications,
  useUnreadCount,
  useMarkAllNotificationsRead,
} from '@/hooks/useNotifications';
import { timeAgo } from '@/lib/formatters';
import { processAvatarUrl } from '@/lib/mediaUtils';
import type { Notification as SdkNotification } from '@/types';

/** Extract display fields from an SDK notification. */
function parseNotification(n: SdkNotification) {
  const raw = n as Record<string, unknown>;
  const data = (n.data ?? {}) as Record<string, unknown>;
  const user = data.user as Record<string, unknown> | undefined;

  const fromUserName = raw.fromUserName as string | undefined;
  const fromUserUsername = raw.fromUserUsername as string | undefined;
  const notificationType = n.type || (raw.notificationType as string) || 'system';
  const actorAvatar = (data.actorAvatar as string) || (user?.avatarUrl as string) || (user?.avatar as string);

  return {
    id: (raw.notificationId as string) || n.id,
    type: notificationType,
    title: fromUserName || fromUserUsername || (user?.displayName as string) || (user?.name as string) || (user?.username as string) || (data.title as string) || 'Notification',
    message: (raw.message as string) || (data.message as string) || (data.body as string) || getDefaultMessage(notificationType),
    time: n.createdAt ? timeAgo(n.createdAt) : '',
    avatar: processAvatarUrl(actorAvatar) || undefined,
    read: n.readAt != null || (raw.read as boolean) === true,
  };
}

function getDefaultMessage(type: string): string {
  switch (type) {
    case 'like': return 'liked your post';
    case 'comment': return 'commented on your post';
    case 'follow': return 'started following you';
    case 'group_invite': return 'invited you to join';
    case 'mention': return 'mentioned you';
    case 'share': return 'shared your post';
    default: return '';
  }
}

function getNotificationIcon(type: string) {
  switch (type) {
    case 'like':
      return <Heart size={14} className="text-[#FF4D4D]" fill="#FF4D4D" />;
    case 'comment':
      return <MessageCircle size={14} className="text-[#A38BFF]" fill="#A38BFF" />;
    case 'follow':
      return <UserPlus size={14} className="text-[#1DB954]" />;
    case 'group_invite':
      return <Users size={14} className="text-[#25F4EE]" />;
    case 'mention':
      return <Bell size={14} className="text-[#F5F7FF]" />;
    case 'system':
      return <Star size={14} className="text-[#FFD700]" fill="#FFD700" />;
    case 'share':
      return <Share2 size={14} className="text-[#A38BFF]" />;
    default:
      return <Bell size={14} className="text-[#A7B0C8]" />;
  }
}

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSeeAll: () => void;
  /** Ref to the bell button for desktop positioning */
  anchorRef?: React.RefObject<HTMLButtonElement>;
}

export default function NotificationPanel({ isOpen, onClose, onSeeAll, anchorRef }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  const { data: notificationsData, isLoading } = useNotifications({ unreadOnly: false });
  const { data: unreadCount = 0 } = useUnreadCount();
  const markAllReadMutation = useMarkAllNotificationsRead();

  // Get first 5 notifications
  const rawNotifications = notificationsData?.pages.flatMap(
    (page) => (page.data as SdkNotification[]) ?? []
  ) ?? [];
  const notifications = rawNotifications.slice(0, 5).map(parseNotification);

  // Check if desktop (sm breakpoint = 640px)
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 640;

  // Animation
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      gsap.fromTo(
        backdropRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.2 }
      );
      gsap.fromTo(
        panelRef.current,
        { y: -10, opacity: 0, scale: isDesktop ? 0.95 : 1 },
        { y: 0, opacity: 1, scale: 1, duration: 0.2, ease: 'power2.out' }
      );
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isDesktop]);

  const handleClose = () => {
    gsap.to(backdropRef.current, { opacity: 0, duration: 0.15 });
    gsap.to(panelRef.current, {
      y: -10,
      opacity: 0,
      scale: isDesktop ? 0.95 : 1,
      duration: 0.15,
      onComplete: onClose,
    });
  };

  const handleMarkAllRead = () => {
    // Pass the first (most recent) notification ID to mark all up to that point
    const firstNotificationId = notifications[0]?.id;
    if (firstNotificationId) {
      markAllReadMutation.mutate(firstNotificationId);
    }
  };

  const handleSeeAll = () => {
    handleClose();
    setTimeout(onSeeAll, 150);
  };

  if (!isOpen) return null;

  // Calculate desktop dropdown position
  const getDesktopPosition = () => {
    if (!anchorRef?.current || !isDesktop) return {};
    const rect = anchorRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    };
  };

  const desktopPos = getDesktopPosition();

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/50 sm:bg-black/30"
        onClick={handleClose}
      />

      {/* Panel - Mobile: full-width top sheet, Desktop: dropdown */}
      <div
        ref={panelRef}
        className="absolute bg-[#141821] shadow-2xl flex flex-col
          top-0 left-0 right-0 rounded-b-2xl max-h-[70vh]
          sm:top-auto sm:left-auto sm:right-auto sm:rounded-2xl sm:w-[380px] sm:max-h-[480px] sm:border sm:border-white/10"
        style={{
          paddingTop: isDesktop ? 0 : 'env(safe-area-inset-top, 0px)',
          ...(isDesktop ? { top: desktopPos.top, right: desktopPos.right } : {}),
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-[#F5F7FF]" style={{ fontFamily: 'Space Grotesk' }}>
              Notifications
            </h2>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-[#A38BFF] rounded-full text-[10px] font-bold text-[#0B0D10]">
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markAllReadMutation.isPending}
                className="px-3 py-1.5 bg-[#0B0D10] rounded-full text-xs text-[#A7B0C8] flex items-center gap-1 hover:bg-[#1a1f2a] transition-colors"
              >
                {markAllReadMutation.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Check size={12} />
                )}
                <span>Mark all read</span>
              </button>
            )}
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-[#0B0D10] flex items-center justify-center text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-[calc(70vh-120px)]">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-[#A38BFF]" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell size={32} className="text-[#2A2F3A] mx-auto mb-2" />
              <p className="text-[#A7B0C8] text-sm">No notifications yet</p>
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 flex items-start gap-3 border-b border-white/[0.04] ${
                    !notification.read ? 'bg-[#A38BFF]/5' : ''
                  }`}
                >
                  {/* Avatar or Icon */}
                  <div className="relative flex-shrink-0">
                    {notification.avatar ? (
                      <div className="relative">
                        <img
                          src={notification.avatar}
                          alt={notification.title}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#141821] rounded-full flex items-center justify-center">
                          {getNotificationIcon(notification.type)}
                        </div>
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#0B0D10] flex items-center justify-center">
                        {getNotificationIcon(notification.type)}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#F5F7FF] leading-snug">
                      <span className="font-semibold">{notification.title}</span>{' '}
                      <span className="text-[#A7B0C8]">{notification.message}</span>
                    </p>
                    <p className="text-xs text-[#A7B0C8] mt-0.5">{notification.time}</p>
                  </div>

                  {/* Unread indicator */}
                  {!notification.read && (
                    <span className="w-2 h-2 bg-[#A38BFF] rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-white/[0.06]">
          <button
            onClick={handleSeeAll}
            className="w-full py-2.5 bg-[#A38BFF] rounded-xl text-sm font-medium text-[#0B0D10] flex items-center justify-center gap-1 hover:bg-[#8B6FE8] transition-colors"
          >
            See all notifications
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
